import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";

const AddCommitteeMemberModal = ({
  isOpen,
  onClose,
  onMemberAdded,
  onMemberUpdated,
  memberToEdit,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    designation: "",
    image: null,
    imagePreview: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const authToken = Cookies.get("authToken");

  useEffect(() => {
    if (memberToEdit) {
      setFormData({
        name: memberToEdit.name || "",
        email: memberToEdit.email || "",
        designation: memberToEdit.designation || "",
        image: null,
        imagePreview: memberToEdit.image || ""
      });
    } else {
      setFormData({
        name: "",
        email: "",
        designation: "",
        image: null,
        imagePreview: ""
      });
    }
    setError(null);
  }, [memberToEdit]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") {
      const file = files[0];
      if (file) {
        setFormData(prev => ({
          ...prev,
          image: file,
          imagePreview: URL.createObjectURL(file)
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    const isEditing = !!memberToEdit;
    const url = isEditing
      ? `${process.env.REACT_APP_BACKEND_URL}/apis/admin/updateExecutiveCommittee/${memberToEdit._id}`
      : `${process.env.REACT_APP_BACKEND_URL}/apis/admin/createExecutive`;
  
    const method = isEditing ? "PUT" : "POST";
  
    try {
      setLoading(true);
      setError(null);
      
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("designation", formData.designation);
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formDataToSend,
      });
  
      const data = await response.json();
  
      if (!response.ok || !data.success) {
        throw new Error(data.message || "Something went wrong");
      }
  
      if (isEditing) {
        onMemberUpdated(data.data);
      } else {
        onMemberAdded(data.data);
      }
  
      onClose();
    } catch (err) {
      console.error("Error: " + err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            {memberToEdit ? "Edit Committee Member" : "Add Committee Member"}
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Name *"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700"
            required
          />
          
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email (optional)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700"
          />
          
          <select
            name="designation"
            value={formData.designation}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700"
          >
            <option value="">Select Designation *</option>
            <option value="President">President</option>
            <option value="Dean">Dean</option>
            <option value="Faculty">Faculty</option>
            <option value="Secretary">Secretary</option>
            <option value="Advisor">Advisor</option>
          </select>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Image
            </label>
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700"
            />
          </div>
          
          {formData.imagePreview && (
            <div className="mb-4 flex justify-center">
              <div className="relative">
                <img 
                  src={formData.imagePreview} 
                  alt="Preview" 
                  className="h-24 w-24 object-cover rounded-full border-2 border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, image: null, imagePreview: "" }))}
                  className="absolute -top-2 -right-2 bg-gray-200 text-gray-600 rounded-full p-1 hover:bg-gray-300 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? (memberToEdit ? "Updating..." : "Adding...") : (memberToEdit ? "Update" : "Add")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCommitteeMemberModal;